// =====================================================
// ATTENTION SURFACE API
// =====================================================
// v2 of the readiness checklist client, generalized to the attention engine
// (attention-surface iteration 1). Points at /landlord/api/attention/* — the v1
// /landlord/api/readiness/tasks* paths are served by the same handlers (see
// apps/api/src/routes/manager/attention.routes.ts) but this client no longer uses them.

import { z } from 'zod';
import { apiFetch } from './api.core';
import {
  attentionItemResponseSchema,
  attentionSummarySchema,
  attentionItemsResponseSchema,
} from '@resida/api-types/attention';

// Derived from attentionItemResponseSchema in @resida/api-types/attention
export type AttentionItem = z.infer<typeof attentionItemResponseSchema>;

// Derived from attentionSummarySchema in @resida/api-types/attention
export type AttentionSummary = z.infer<typeof attentionSummarySchema>;

// Derived from attentionItemsResponseSchema in @resida/api-types/attention
export type AttentionItemsResponse = z.infer<typeof attentionItemsResponseSchema>;

/**
 * Get all attention items with preference status, summary counts, and the list of any
 * providers that failed to evaluate this request (`degraded` — BR-25). `items` includes
 * dismissed/snoozed items (marked via `preference`); callers that only want the active set
 * filter on `preference === null`.
 */
export async function getAttentionItems(): Promise<AttentionItemsResponse> {
  return apiFetch('/landlord/api/attention/items');
}

/**
 * Permanently dismiss an attention item. Refused (400) for items carrying a
 * `statutoryDeadline` (BR-17) — the caller should not offer this control for those items.
 */
export async function dismissAttentionItem(taskHash: string): Promise<void> {
  return apiFetch(`/landlord/api/attention/items/${taskHash}/dismiss`, { method: 'POST' });
}

/**
 * Snooze an attention item for 1, 3, 7, or 30 days (truncated server-side at any statutory
 * deadline — BR-16).
 */
export async function snoozeAttentionItem(taskHash: string, days: 1 | 3 | 7 | 30): Promise<void> {
  return apiFetch(`/landlord/api/attention/items/${taskHash}/snooze`, {
    method: 'POST',
    body: JSON.stringify({ days }),
  });
}

/**
 * Remove a dismiss or snooze preference for an item (idempotent).
 */
export async function removeAttentionItemPreference(taskHash: string): Promise<void> {
  return apiFetch(`/landlord/api/attention/items/${taskHash}/preference`, { method: 'DELETE' });
}
